/**
 * Page de bienvenue après un achat direct (retour Stripe).
 *
 * URL : /bienvenue?session_id=cs_...
 * Accessible sans compte. Affiche le produit acheté, préremplit l'email collecté
 * par Stripe et propose la connexion OTP. Le compte a (normalement) été créé par
 * le webhook ; l'OTP sert alors de simple connexion. Une fois connecté, on
 * attend brièvement l'activation de l'accès puis on redirige vers le dashboard.
 */

import { useEffect, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CompassLogo } from '@jeraisonne/ui';
import { OtpAuthForm } from '@/components/otp-auth-form';
import { useAuth } from '@/lib/auth';
import { supabase, supabaseUrl, supabaseAnonKey } from '@/lib/supabase';

type Summary = { email: string | null; product_label: string; kind: string };

async function fetchSummary(sessionId: string): Promise<Summary | null> {
  try {
    const res = await fetch(
      `${supabaseUrl}/functions/v1/get-checkout-summary?session_id=${encodeURIComponent(sessionId)}`,
      { headers: { apikey: supabaseAnonKey, Authorization: `Bearer ${supabaseAnonKey}` } },
    );
    if (!res.ok) return null;
    return (await res.json()) as Summary;
  } catch {
    return null;
  }
}

async function hasAccess(userId: string): Promise<boolean> {
  const nowIso = new Date().toISOString();
  const { data: pass } = await supabase
    .from('access_rights')
    .select('id')
    .eq('user_id', userId)
    .gt('expires_at', nowIso)
    .limit(1)
    .maybeSingle();
  if (pass) return true;
  const { data: sub } = await supabase.rpc('has_active_subscription');
  return !!sub;
}

export function BienvenuePage() {
  const sessionId = new URLSearchParams(window.location.search).get('session_id') ?? '';
  const { session } = useAuth();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const activationStarted = useRef(false);

  // Résumé de la session Stripe (libellé produit + email à préremplir).
  useEffect(() => {
    if (!sessionId) {
      setSummaryLoading(false);
      return;
    }
    let cancelled = false;
    fetchSummary(sessionId).then((s) => {
      if (!cancelled) {
        setSummary(s);
        setSummaryLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Une fois connecté : on attend l'activation (webhook async) puis on redirige.
  useEffect(() => {
    if (!session || activationStarted.current) return;
    activationStarted.current = true;
    setActivating(true);

    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      const ok = await hasAccess(session.user.id);
      if (cancelled) return;
      if (ok || tries >= 5) {
        window.location.href = '/';
        return;
      }
      window.setTimeout(tick, 1500);
    };
    tick();

    return () => {
      cancelled = true;
    };
  }, [session]);

  const productLabel = summary?.product_label ?? 'ton accès Jeraisonne';

  return (
    <div className="min-h-dvh bg-transparent">
      <header className="flex justify-center px-6 py-8">
        <CompassLogo size="lg" wordmarkClassName="text-ink-900" />
      </header>

      <main className="mx-auto flex w-full max-w-md flex-col px-6 pb-16 pt-4 sm:pt-12">
        {/* Bandeau « tu viens d'acheter X » */}
        <div className="border-primary-200 bg-primary-50 mb-6 rounded-2xl border p-5 text-center">
          <p className="text-3xl">🎉</p>
          <p className="text-primary-700 mt-2 font-serif text-xl font-semibold">
            Merci pour ton achat !
          </p>
          <p className="text-ink-600 mt-1 text-sm">
            <strong className="text-ink-800">{productLabel}</strong> t&apos;attend. Connecte-toi
            pour l&apos;activer.
          </p>
        </div>

        <Card className="border-ink-100/60 shadow-lg">
          {activating ? (
            <CardHeader>
              <div className="flex flex-col items-center gap-4 py-6 text-center">
                <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
                <div>
                  <CardTitle>Activation en cours…</CardTitle>
                  <CardDescription>On prépare ton accès, un instant.</CardDescription>
                </div>
              </div>
            </CardHeader>
          ) : summaryLoading ? (
            <CardHeader>
              <div className="flex justify-center py-10">
                <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
              </div>
            </CardHeader>
          ) : (
            <OtpAuthForm
              lockedEmail={summary?.email ?? undefined}
              hideOAuth
              hideModeToggle
              renderHeader={({ step }) =>
                step === 'otp' ? (
                  <>
                    <CardTitle>Vérifie ta boîte mail</CardTitle>
                    <CardDescription>
                      Entre le code à 6 chiffres envoyé à{' '}
                      <strong className="text-ink-700">{summary?.email}</strong>.
                    </CardDescription>
                  </>
                ) : (
                  <>
                    <CardTitle>Active ton accès</CardTitle>
                    <CardDescription>
                      {summary?.email
                        ? 'Reçois un code sur ton email de paiement pour te connecter.'
                        : 'Saisis ton email de paiement pour te connecter.'}
                    </CardDescription>
                  </>
                )
              }
            />
          )}
        </Card>
      </main>
    </div>
  );
}
